import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  FiCalendar,
  FiEye,
  FiEyeOff,
  FiLayers,
  FiLock,
  FiLogIn,
  FiMail,
  FiShield,
  FiUsers,
} from "react-icons/fi";

import { useAuth } from "../../contexts/AuthContext";
import LoadingScreen from "../../components/LoadScreen";

// ==========================================
// ESTILOS GERAIS
// ==========================================

const Container = styled.main`
  display: flex;
  min-height: 100vh;
  width: 100%;
  box-sizing: border-box;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    sans-serif;
  background-color: #f8fafc;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`;

// ==========================================
// PAINEL ESQUERDO
// ==========================================

const LeftPanel = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 4rem;
  position: relative;
  overflow: hidden;
  background: linear-gradient(
    135deg,
    #e0f2fe 0%,
    #f0f9ff 50%,
    #e6f4f1 100%
  );

  &::after {
    content: "";
    position: absolute;
    bottom: -100px;
    left: -100px;
    width: 400px;
    height: 400px;
    background: rgba(14, 165, 233, 0.15);
    border-radius: 50%;
    filter: blur(80px);
    pointer-events: none;
  }

  @media (max-width: 968px) {
    display: none;
  }
`;

const BrandBox = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 3rem;
  position: relative;
  z-index: 1;
`;

const BrandIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.65);
  color: #0284c7;
`;

const LogoText = styled.div`
  h1 {
    margin: 0;
    color: #0284c7;
    font-size: 2rem;
    font-weight: 700;
    letter-spacing: -0.04em;

    span {
      color: #10b981;
    }
  }

  p {
    margin: 0;
    color: #64748b;
    font-size: 0.875rem;
  }
`;

const HeroTitle = styled.h2`
  max-width: 420px;
  margin: 0 0 2.5rem;
  color: #1e293b;
  font-size: 1.85rem;
  font-weight: 600;
  line-height: 1.3;
  position: relative;
  z-index: 1;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  max-width: 420px;
  position: relative;
  z-index: 1;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const IconBadge = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 10px;
  background-color: #e0f2fe;
  color: #0284c7;
`;

const FeatureText = styled.div`
  h4 {
    margin: 0 0 0.25rem;
    color: #1e293b;
    font-size: 0.95rem;
    font-weight: 600;
  }

  p {
    margin: 0;
    color: #64748b;
    font-size: 0.85rem;
  }
`;

// ==========================================
// PAINEL DIREITO
// ==========================================

const RightPanel = styled.section`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background-color: #f8fafc;

  @media (max-width: 968px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const LoginCard = styled.div`
  width: 100%;
  max-width: 440px;
  padding: 2.5rem;
  background-color: #ffffff;
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  box-shadow:
    0 10px 25px -5px rgba(0, 0, 0, 0.05),
    0 8px 10px -6px rgba(0, 0, 0, 0.01);

  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const LoginHeader = styled.div`
  margin-bottom: 2rem;

  h2 {
    margin: 0 0 0.5rem;
    color: #1e293b;
    font-size: 1.6rem;
    font-weight: 700;
  }

  p {
    margin: 0;
    color: #64748b;
    font-size: 0.875rem;
    line-height: 1.5;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    color: #334155;
    font-size: 0.875rem;
    font-weight: 600;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.span`
  position: absolute;
  left: 1rem;
  display: flex;
  align-items: center;
  color: #94a3b8;
  pointer-events: none;
`;

const Input = styled.input`
  width: 100%;
  min-height: 46px;
  padding: 0.75rem 2.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  color: #1e293b;
  background-color: #ffffff;
  font-family: inherit;
  font-size: 0.925rem;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &::placeholder {
    color: #cbd5e1;
  }

  &:focus {
    border-color: #0284c7;
    box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.15);
  }

  &:disabled {
    cursor: not-allowed;
    background-color: #f8fafc;
  }
`;

const TogglePasswordButton = styled.button`
  position: absolute;
  right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #64748b;
  }

  &:focus-visible {
    outline: 2px solid #0284c7;
    outline-offset: 3px;
    border-radius: 4px;
  }
`;

const ForgotPassword = styled.a`
  align-self: flex-end;
  margin-top: -0.25rem;
  color: #0284c7;
  font-size: 0.825rem;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid #0284c7;
    outline-offset: 3px;
    border-radius: 3px;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.85rem;
  border: none;
  border-radius: 8px;
  background-color: #0284c7;
  color: #ffffff;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    transform 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #0369a1;
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 3px solid rgba(2, 132, 199, 0.3);
    outline-offset: 3px;
  }

  &:disabled {
    background-color: #94a3b8;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  padding: 0.75rem;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background-color: #fef2f2;
  color: #dc2626;
  font-size: 0.85rem;
  line-height: 1.4;
  text-align: center;
`;

const SecurityNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2.5rem;
  color: #64748b;
  font-size: 0.775rem;
  line-height: 1.4;
  text-align: center;

  svg {
    flex-shrink: 0;
    color: #64748b;
  }
`;

// ==========================================
// COMPONENTE PRINCIPAL
// ==========================================


export default function Login() {
  const {
    login: authLogin,
    authenticated,
    loading,
  } = useAuth();

  const navigate = useNavigate();

  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Enquanto o contexto verifica se existe uma sessão ativa
  if (loading) {
    return <LoadingScreen />;
  }

  // Se já estiver autenticado, não mostra novamente a tela de login
  if (authenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    setError("");
    setSubmitting(true);

    try {
      await authLogin(login, password);

      navigate("/dashboard", {
        replace: true,
      });
      } catch (err) {
    console.error("ERRO LOGIN:", err);
    console.error("STATUS:", err.response?.status);
    console.error("RESPOSTA:", err.response?.data);

    if (err.response?.status === 422) {
      setError("Login ou senha inválidos.");
    } else if (err.response?.status === 419) {
      setError("A sessão de segurança expirou.");
    } else if (err.response?.status === 401) {
      setError("Login ou senha inválidos.");
    } else if (err.response?.status === 500) {
      setError("Erro interno no servidor.");
    } else {
      setError(
        `Erro ao realizar login. Status: ${err.response?.status || "sem resposta"}`
      );
    }
  } finally {
      setSubmitting(false);
    }
  }

  return (
    <Container>
      {/* ========================================
          PAINEL ESQUERDO
      ======================================== */}

      <LeftPanel>
        <BrandBox>
          <BrandIcon>
            <FiLayers size={30} />
          </BrandIcon>

          <LogoText>
            <h1>
              SisGec<span>Med</span>
            </h1>

            <p>Sistema de Gestão em Saúde</p>
          </LogoText>
        </BrandBox>

        <HeroTitle>
          Mais organização para sua clínica, mais cuidado para seus pacientes.
        </HeroTitle>

        <FeatureList>
          <FeatureItem>
            <IconBadge>
              <FiCalendar size={20} />
            </IconBadge>

            <FeatureText>
              <h4>Agendamentos</h4>
              <p>Organize a rotina da sua clínica</p>
            </FeatureText>
          </FeatureItem>

          <FeatureItem>
            <IconBadge>
              <FiUsers size={20} />
            </IconBadge>

            <FeatureText>
              <h4>Gestão de Pacientes</h4>
              <p>Acompanhe e cuide de cada detalhe</p>
            </FeatureText>
          </FeatureItem>

          <FeatureItem>
            <IconBadge>
              <FiShield size={20} />
            </IconBadge>

            <FeatureText>
              <h4>Segurança</h4>
              <p>Seus dados protegidos e sempre acessíveis</p>
            </FeatureText>
          </FeatureItem>
        </FeatureList>
      </LeftPanel>

      {/* ========================================
          PAINEL DIREITO
      ======================================== */}

      <RightPanel>
        <LoginCard>
          <LoginHeader>
            <h2>Bem-vindo(a)!</h2>

            <p>
              Faça login para acessar o sistema SisGecMed e continuar.
            </p>
          </LoginHeader>

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <label htmlFor="login">Login</label>

              <InputWrapper>
                <InputIcon>
                  <FiMail size={18} />
                </InputIcon>

                <Input
                  id="login"
                  name="login"
                  type="text"
                  value={login}
                  onChange={(event) => setLogin(event.target.value)}
                  placeholder="Digite seu login"
                  autoComplete="username"
                  disabled={submitting}
                  required
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <label htmlFor="password">Senha</label>

              <InputWrapper>
                <InputIcon>
                  <FiLock size={18} />
                </InputIcon>

                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Digite sua senha"
                  autoComplete="current-password"
                  disabled={submitting}
                  required
                />

                <TogglePasswordButton
                  type="button"
                  onClick={() =>
                    setShowPassword((current) => !current)
                  }
                  aria-label={
                    showPassword ? "Ocultar senha" : "Mostrar senha"
                  }
                  title={
                    showPassword ? "Ocultar senha" : "Mostrar senha"
                  }
                  disabled={submitting}
                >
                  {showPassword ? (
                    <FiEyeOff size={18} />
                  ) : (
                    <FiEye size={18} />
                  )}
                </TogglePasswordButton>
              </InputWrapper>
            </FormGroup>

            <ForgotPassword href="/recuperar-senha">
              Esqueceu sua senha?
            </ForgotPassword>

            {error && (
              <ErrorMessage role="alert">
                {error}
              </ErrorMessage>
            )}

            <SubmitButton type="submit" disabled={submitting}>
              <FiLogIn size={18} />

              {submitting ? "Validando acesso..." : "Entrar"}
            </SubmitButton>
          </Form>

          <SecurityNotice>
            <FiShield size={16} />

            <span>
              Este sistema é de uso exclusivo para profissionais autorizados
              da clínica.
            </span>
          </SecurityNotice>
        </LoginCard>
      </RightPanel>
    </Container>
  );
}

